import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Lock, Eye, FileText, UserCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background" data-testid="privacy-policy-page">
      {/* Header */}
      <div className="border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link to="/">
            <Button variant="ghost" size="sm" className="gap-2">
              <ArrowLeft className="h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-12 w-12 rounded-sm bg-primary/10 flex items-center justify-center">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-heading font-semibold text-foreground">Privacy Policy</h1>
            <p className="text-sm text-muted-foreground">Last updated: December 2025</p>
          </div>
        </div>

        <div className="prose prose-slate max-w-none space-y-8">
          {/* Introduction */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4 flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary" />
              Introduction
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              My Budget Tracker ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our financial tracking application. We understand that your financial data is sensitive, and we treat it with the utmost care and security.
            </p>
          </section>

          {/* Information We Collect */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4 flex items-center gap-2">
              <Eye className="h-5 w-5 text-primary" />
              Information We Collect
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <div>
                <h3 className="font-medium text-foreground mb-2">Personal Information</h3>
                <ul className="list-disc list-inside space-y-1">
                  <li>Name and email address (for account creation)</li>
                  <li>Password (stored securely using industry-standard encryption)</li>
                  <li>Phone number (optional, for account recovery)</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-2">Financial Information</h3>
                <ul className="list-disc list-inside space-y-1">
                  <li>Transaction data you manually enter or import</li>
                  <li>Category preferences and budget settings</li>
                  <li>Income and expense records</li>
                </ul>
              </div>
              <div>
                <h3 className="font-medium text-foreground mb-2">Usage Information</h3>
                <ul className="list-disc list-inside space-y-1">
                  <li>Login activity and session data</li>
                  <li>Feature usage patterns (anonymized)</li>
                  <li>Device and browser information</li>
                </ul>
              </div>
            </div>
          </section>

          {/* How We Use Your Information */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4 flex items-center gap-2">
              <UserCheck className="h-5 w-5 text-primary" />
              How We Use Your Information
            </h2>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>To provide and maintain our financial tracking services</li>
              <li>To process your transactions and generate insights</li>
              <li>To personalize your experience with relevant features</li>
              <li>To communicate important updates about your account</li>
              <li>To improve our services based on usage patterns</li>
              <li>To detect and prevent fraud or unauthorized access</li>
              <li>To comply with legal obligations</li>
            </ul>
          </section>

          {/* Data Security */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4 flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" />
              Data Security
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>We implement robust security measures to protect your data:</p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Encryption at Rest:</strong> All stored data is encrypted using AES-256 encryption</li>
                <li><strong>Encryption in Transit:</strong> All data transfers use TLS 1.3 protocol</li>
                <li><strong>Password Security:</strong> Passwords are hashed using bcrypt algorithm</li>
                <li><strong>Access Controls:</strong> Strict authentication required for all data access</li>
                <li><strong>Regular Audits:</strong> Periodic security assessments and vulnerability testing</li>
                <li><strong>Activity Logging:</strong> Comprehensive audit trails for all account activities</li>
              </ul>
            </div>
          </section>

          {/* Data Sharing */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4">Data Sharing and Disclosure</h2>
            <div className="space-y-4 text-muted-foreground">
              <p className="font-medium text-foreground">We DO NOT sell, rent, or share your personal financial data with third parties for marketing purposes.</p>
              <p>We may share data only in these limited circumstances:</p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Service Providers:</strong> Trusted partners who help operate our service (payment processing, cloud hosting) under strict confidentiality agreements</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or government request</li>
                <li><strong>With Your Consent:</strong> When you explicitly authorize sharing</li>
                <li><strong>Business Transfers:</strong> In case of merger or acquisition, with continued privacy protections</li>
              </ul>
            </div>
          </section>

          {/* Your Rights */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4">Your Rights (GDPR & Australian Privacy Act Compliant)</h2>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li><strong>Access:</strong> Request a copy of all data we hold about you</li>
              <li><strong>Correction:</strong> Update or correct inaccurate information</li>
              <li><strong>Deletion:</strong> Request permanent deletion of your account and data</li>
              <li><strong>Export:</strong> Download your data in a portable format</li>
              <li><strong>Restriction:</strong> Limit how we process your data</li>
              <li><strong>Objection:</strong> Object to certain data processing activities</li>
            </ul>
            <p className="mt-4 text-muted-foreground">
              To exercise any of these rights, contact us at privacy@mybudgettracker.com.au or use the account settings in the application.
            </p>
          </section>

          {/* Cookies */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4">Cookies and Tracking</h2>
            <div className="space-y-4 text-muted-foreground">
              <p>We use essential cookies required for the application to function:</p>
              <ul className="list-disc list-inside space-y-2">
                <li><strong>Authentication Cookies:</strong> Keep you logged in securely</li>
                <li><strong>Session Cookies:</strong> Remember your preferences during a session</li>
              </ul>
              <p className="mt-4">
                We may use analytics services (such as Google Analytics) to understand how users interact with our application. This data is anonymized and used solely to improve our services.
              </p>
            </div>
          </section>

          {/* Data Retention */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4">Data Retention</h2>
            <p className="text-muted-foreground">
              We retain your data for as long as your account is active. If you delete your account, all associated data is permanently removed within 30 days, except where we're legally required to retain certain records.
            </p>
          </section>

          {/* Children's Privacy */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4">Children's Privacy</h2>
            <p className="text-muted-foreground">
              Our service is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If you believe a child has provided us with personal information, please contact us immediately.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4">Contact Us</h2>
            <p className="text-muted-foreground">
              For any privacy-related questions or concerns, please contact us at:
            </p>
            <div className="mt-4 p-4 bg-muted/50 rounded-sm text-muted-foreground">
              <p><strong>Email:</strong> privacy@mybudgettracker.com.au</p>
              <p><strong>Address:</strong> My Budget Tracker Pty Ltd, Sydney, NSW, Australia</p>
            </div>
          </section>

          {/* Updates */}
          <section className="bg-card border border-border rounded-sm p-6">
            <h2 className="text-xl font-heading font-medium text-foreground mb-4">Policy Updates</h2>
            <p className="text-muted-foreground">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting a notice on our website or sending you an email. Your continued use of the service after such modifications constitutes your acknowledgment of the updated policy.
            </p>
          </section>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-border mt-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-center">
          <p className="text-sm text-muted-foreground">
            © 2026 My Budget Tracker. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
